import React, { useEffect, useState } from "react";
import packageJson from "../../../package.json";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  Badge,
  Avatar,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DirectionsIcon from "@mui/icons-material/Directions";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AssignmentIcon from "@mui/icons-material/Assignment";

import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationMenu from "./NotificationMenu"; 
import ProfileMenu from "./ProfileMenu"; 

import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../../assets/utils/axiosConfig";
import logo from "../../assets/images/logo.png";

const menuConfig = [
  {
    section: "Inicio",
    items: [{ title: "Inicio", path: "/inicio", icon: <DashboardCustomizeOutlinedIcon /> }],
  },
  {
    section: "Servicios",
    items: [
      { title: "Rutas", path: "/rutas", icon: <DirectionsIcon /> },
      { title: "Reportes", path: "/dashboard", icon: <AssessmentIcon /> },
      { title: "Planificaciones", path: "/planificaciones", icon: <AssignmentIcon /> },
    ],
  },
  {
    section: "Administración",
    items: [
      { title: "Usuarios", path: "/usuarios", icon: <ManageAccountsIcon /> },
      { title: "Destinos", path: "/destinos", icon: <EditLocationIcon /> },
      { title: "Recorridos", path: "/recorridos", icon: <AltRouteIcon /> },
    ],
  },
  {
    section: "Configuración",
    items: [
      { title: "Mi Licencia", path: "/milicencia", icon: <SettingsApplicationsIcon /> },
      { title: "Configuración", path: "/configuracion", icon: <SettingsApplicationsIcon /> }],
  },
];

const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [modulosPermitidos, setModulosPermitidos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [anchorElNotif, setAnchorElNotif] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const [unreadCount, setUnreadCount] = useState(0);
  
  // 🔄 Estado local del usuario sincronizado con el LocalStorage
  const [usuario, setUsuario] = useState({ nombre: "", inicial: "U", avatarUrl: "" });

  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");

  const toggleSidebar = () => setOpen(!open);

  const handleNotifMenuOpen = (event) => setAnchorElNotif(event.currentTarget);
  const handleNotifMenuClose = () => setAnchorElNotif(null);

  const handleUserMenuOpen = (event) => setAnchorElUser(event.currentTarget);
  const handleUserMenuClose = () => setAnchorElUser(null);

  /* 🔒 Sincronizar datos del LocalStorage al montar el componente */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      logout();
      return;
    }

    // ⚡ Recuperar y parsear el objeto userData guardado en localStorage
    const rawUserData = localStorage.getItem("userData");
    if (rawUserData) {
      try {
        const parsedUser = JSON.parse(rawUserData);
        
        const nombreValido = parsedUser.nombre || parsedUser.user || "";
        
        setUsuario({
          nombre: nombreValido,
          inicial: nombreValido ? nombreValido.charAt(0).toUpperCase() : "U",
          avatarUrl: parsedUser.avatarUrl || parsedUser.avatar || ""
        });
      } catch (error) {
        console.error("Error al parsear el userData del localStorage:", error);
      }
    }

    const fetchData = async () => {
      try {
        // 🔥 El interceptor inyecta el token automáticamente
        const resModulos = await api.get(process.env.REACT_APP_OBTENERMODULOS);

        if (resModulos.data.success) {
          setModulosPermitidos(resModulos.data.modulos.map((m) => m.ruta.toLowerCase()));
        } else {
          logout();
          return;
        }

      } catch (error) {
        console.error("Error general en carga:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const isAllowed = (path) => modulosPermitidos.includes(path.toLowerCase());

  const getButtonStyle = (path) => {
    const active = location.pathname.toLowerCase() === path.toLowerCase();
    return {
      borderRadius: "8px",
      mb: "2px",
      px: collapsed ? 1 : 1.5,
      py: 0.6,
      minHeight: "34px",
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      gap: "10px",
      bgcolor: active ? "#2FA4B8" : "transparent",
      color: active ? "#fff" : "#475569",
      transition: "all 0.18s ease",
      "&:hover": {
        bgcolor: active ? "#2FA4B8" : "#F8FAFC",
        color: "#0F172A",
        transform: "translateX(2px)",
      },
    };
  };

  const logout = () => {
    localStorage.clear();
    navigate("/");
  };

  const drawerWidth = collapsed ? 64 : 220;

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#0F2F44",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
          borderBottom: "1px solid #1E293B",
          zIndex: 1100,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            onClick={() => (isMobile ? toggleSidebar() : setCollapsed(!collapsed))}
            sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography
              sx={{
                fontFamily: "'Orbitron', sans-serif",
                fontWeight: 800,
                letterSpacing: "6px",
                fontSize: { xs: "18px", sm: "24px" },
                background: "linear-gradient(90deg, #2FA4B8 0%, #00E5FF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0px 0px 10px rgba(47, 164, 184, 0.3)",
              }}
            >
              CRONOS
            </Typography>
            <Box sx={{ width: "40px", height: "2px", bgcolor: "#2FA4B8", mt: 0.2, borderRadius: "2px" }} />
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Tooltip title="Notificaciones">
              <IconButton color="inherit" onClick={handleNotifMenuOpen} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}>
                <Badge badgeContent={unreadCount} color="error" sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}>
                  <NotificationsIcon sx={{ color: "#9AA3AD" }} />
                </Badge>
              </IconButton>
            </Tooltip>

            <NotificationMenu anchorEl={anchorElNotif} onClose={handleNotifMenuClose} setUnreadCount={setUnreadCount} />

            <Tooltip title="Mi Cuenta">
              <IconButton
                onClick={handleUserMenuOpen}
                sx={{ 
                  p: 0.5, 
                  border: "2px solid #2FA4B8",
                  transition: "0.2s",
                  "&:hover": { transform: "scale(1.05)" }
                }}
              >
                <Avatar 
                  src={usuario.avatarUrl}
                  sx={{ width: 30, height: 30, bgcolor: "#2FA4B8", fontSize: "14px", fontWeight: 600 }}
                >
                  {usuario.inicial}
                </Avatar>
              </IconButton>
            </Tooltip>

            <ProfileMenu anchorEl={anchorElUser} onClose={handleUserMenuClose} />
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={toggleSidebar}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            marginTop: "64px",
            height: "calc(100vh - 64px)",
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            backgroundColor: "#FFFFFF",
            borderRight: "1px solid #E2E8F0",
            transition: "all 0.2s ease",
          },
        }}
      >
        <Box textAlign="center" my={1.2}>
          <img src={logo} alt="Logo" style={{ width: collapsed ? "32px" : "100px", transition: "0.2s", opacity: 0.9 }} />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Paper elevation={0} sx={{ px: 1.2, py: 1 }}>
            {menuConfig.map((section) => {
              const visibleItems = section.items.filter((item) => isAllowed(item.path));
              if (!visibleItems.length) return null;

              return (
                <Box key={section.section} mb={1.5}>
                  {!collapsed && (
                    <Typography sx={{ fontWeight: 500, fontSize: "10px", textTransform: "uppercase", color: "#94A3B8", letterSpacing: "1px", px: 1, mb: 0.5 }}>
                      {section.section}
                    </Typography>
                  )}

                  <List sx={{ p: 0 }}>
                    {visibleItems.map((item) => {
                      const active = location.pathname.toLowerCase() === item.path.toLowerCase();
                      return (
                        <ListItem key={item.path} disablePadding>
                          <Tooltip title={collapsed ? item.title : ""} placement="right">
                            <ListItemButton component={Link} to={item.path} sx={getButtonStyle(item.path)} onClick={isMobile ? toggleSidebar : undefined}>
                              <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 1.5, justifyContent: "center", color: active ? "white" : "#64748B" }}>
                                {item.icon}
                              </ListItemIcon>
                              {!collapsed && <ListItemText primary={item.title} />}
                            </ListItemButton>
                          </Tooltip>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </Paper>
        )}
        <Box
  sx={{
    mt: "auto",
    py: 2,
    px: 1,
    textAlign: "center",
    borderTop: "1px solid #E2E8F0",
  }}
>
  {!collapsed ? (
    <Typography
      sx={{
        fontSize: "11px",
        color: "#94A3B8",
        letterSpacing: "0.3px",
      }}
    >
      Versión {packageJson.version}
    </Typography>
  ) : (
    <Typography
      sx={{
        fontSize: "10px",
        color: "#94A3B8",
      }}
    >
      v{packageJson.version}
    </Typography>
  )}
</Box>
      </Drawer>
    </>
  );
};

export default Sidebar;